import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/phone-input-override.css';
import { mvpApi } from '../services/api';
import { COUNTRIES } from '../data/countries';
import { capitalizeWords, isStrictEmail, normalizeEmail } from '../utils/formInputUtils';
import './ProfileSetup.css';

/**
 * Derives signup method from stored user: phone signup (phone set, email collected)
 * or OAuth (email set, phone collected). For phone signup, phone is read-only and
 * email is editable + must be verified. For OAuth, email is read-only and phone
 * is editable + must be verified via OTP.
 */
function getSignupMethod(storedUser) {
  const hasPhone = !!(storedUser?.phone && storedUser.phone.trim());
  const hasEmail = !!(storedUser?.email && storedUser.email.trim());
  // OAuth users have synthetic phone (google:xxx); they have email from IdP
  if (hasEmail && hasPhone && (storedUser.phone || '').startsWith('google:')) return 'oauth';
  if (hasPhone) return 'phone';
  if (hasEmail) return 'oauth';
  return 'phone';
}

function ProfileSetup({ onComplete, mode = 'setup' }) {
  const { t } = useTranslation();
  const isEditMode = mode === 'edit';

  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [countryOfOrigin, setCountryOfOrigin] = useState('');
  const [signupMethod, setSignupMethod] = useState('phone');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');

  // OAuth: phone verification (Send OTP → enter code → Verify)
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [phoneVerifyLoading, setPhoneVerifyLoading] = useState(false);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(false);

  // Phone signup: email verification (Send code → enter code → Verify)
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('userData') || '{}');
      if (storedUser) {
        setFirstName(storedUser.first_name || storedUser.firstName || '');
        setMiddleName(storedUser.middle_name || '');
        setLastName(storedUser.last_name || storedUser.lastName || '');
        setEmail(storedUser.email || '');
        setPhone(storedUser.phone || '');
        setSignupMethod(getSignupMethod(storedUser));
        if (storedUser.phone_verified && storedUser.phone && !(storedUser.phone || '').startsWith('google:')) {
          setPhoneVerified(true);
        }
        if (storedUser.email_verified) {
          setEmailVerified(true);
        }
        if (storedUser.date_of_birth) {
          const d = new Date(storedUser.date_of_birth);
          if (!Number.isNaN(d.getTime())) {
            setDateOfBirth(d.toISOString().slice(0, 10));
          }
        }
        if (storedUser.country_of_origin) {
          setCountryOfOrigin(storedUser.country_of_origin);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // Resend cooldowns
  useEffect(() => {
    if (phoneResendCooldown <= 0) return;
    const timer = setTimeout(() => setPhoneResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [phoneResendCooldown]);
  useEffect(() => {
    if (emailResendCooldown <= 0) return;
    const timer = setTimeout(() => setEmailResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [emailResendCooldown]);

  const handleRequestPhoneOtp = async (e) => {
    e.preventDefault();
    setError(null);
    const p = (phone && phone.startsWith('+') ? phone : `+${phone}`).trim();
    if (!p || p.length < 10) {
      setError(t('phoneRequired', 'Please enter a valid phone number first.'));
      return;
    }
    setPhoneVerifyLoading(true);
    try {
      await mvpApi.requestProfilePhoneOtp(p);
      setPhoneOtpSent(true);
      setPhoneResendCooldown(120);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : null) || t('errorOccurred', 'Something went wrong.'));
    } finally {
      setPhoneVerifyLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async (e) => {
    e.preventDefault();
    setError(null);
    const p = (phone && phone.startsWith('+') ? phone : `+${phone}`).trim();
    if (!p || !phoneOtpCode.trim()) {
      setError(t('enterCode', 'Please enter the code we sent you.'));
      return;
    }
    setPhoneVerifyLoading(true);
    try {
      const resp = await mvpApi.verifyProfilePhoneOtp(p, phoneOtpCode.trim());
      const user = resp?.data?.user;
      if (user) {
        const safe = {
          ...JSON.parse(localStorage.getItem('userData') || '{}'),
          phone: user.phone,
          phone_verified: user.phone_verified,
        };
        localStorage.setItem('userData', JSON.stringify(safe));
      }
      setPhoneVerified(true);
      if (user?.phone) setPhone(user.phone);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : null) || t('invalidOrExpiredCode', 'Invalid or expired code.'));
    } finally {
      setPhoneVerifyLoading(false);
    }
  };

  const handleRequestEmailOtp = async (e) => {
    e.preventDefault();
    setError(null);
    const e2 = normalizeEmail(email);
    if (!e2) {
      setError(t('emailRequired', 'Please enter your email first.'));
      return;
    }
    if (!isStrictEmail(e2)) {
      setEmailError(t('invalidEmail', 'Please enter a valid email address.'));
      return;
    }
    setEmailVerifyLoading(true);
    try {
      await mvpApi.requestProfileEmailOtp(e2);
      setEmailOtpSent(true);
      setEmailResendCooldown(600);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : null) || t('errorOccurred', 'Something went wrong.'));
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    setError(null);
    const e2 = normalizeEmail(email);
    if (!e2 || !emailOtpCode.trim()) {
      setError(t('enterCode', 'Please enter the code we sent you.'));
      return;
    }
    setEmailVerifyLoading(true);
    try {
      const resp = await mvpApi.verifyProfileEmailOtp(e2, emailOtpCode.trim());
      const user = resp?.data?.user;
      if (user) {
        const safe = {
          ...JSON.parse(localStorage.getItem('userData') || '{}'),
          email: user.email,
          email_verified: user.email_verified,
        };
        localStorage.setItem('userData', JSON.stringify(safe));
      }
      setEmailVerified(true);
      setEmail(user?.email || e2);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : null) || t('invalidOrExpiredCode', 'Invalid or expired code.'));
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!firstName?.trim() || !lastName?.trim()) {
      setError(t('fillAllRequiredFields', 'Please fill in your first name and last name.'));
      return;
    }
    if (signupMethod === 'oauth' && !phoneVerified) {
      setError(t('verifyPhoneFirst', 'Please verify your phone number before continuing.'));
      return;
    }
    if (signupMethod === 'phone' && !emailVerified) {
      setError(t('verifyEmailFirst', 'Please verify your email before continuing.'));
      return;
    }
    if (signupMethod === 'phone' && !email?.trim()) {
      setError(t('emailRequired', 'Please enter and verify your email.'));
      return;
    }
    if (signupMethod === 'phone' && !isStrictEmail(email)) {
      setEmailError(t('invalidEmail', 'Please enter a valid email address.'));
      return;
    }
    if (signupMethod === 'oauth') {
      const p = (phone && phone.startsWith('+') ? phone : `+${phone}`).trim();
      if (!p || p.length < 10) {
        setError(t('phoneRequired', 'Please enter and verify your phone number.'));
        return;
      }
    }
    if (!dateOfBirth?.trim()) {
      setError(t('dobRequired', 'Please enter your date of birth.'));
      return;
    }
    if (!countryOfOrigin) {
      setError(t('countryRequired', 'Please select your country of origin.'));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        first_name: firstName.trim(),
        middle_name: middleName?.trim() || null,
        last_name: lastName.trim(),
        date_of_birth: dateOfBirth,
        country_of_origin: countryOfOrigin,
      };
      if (signupMethod === 'phone') {
        payload.email = normalizeEmail(email);
      } else {
        payload.phone = phone.startsWith('+') ? phone : `+${phone}`;
      }

      const resp = await mvpApi.updatePersonalDetails(payload);
      const updatedUser = resp?.data?.user;
      if (updatedUser) {
        const safeUser = {
          id: updatedUser.id,
          email: updatedUser.email,
          email_verified: updatedUser.email_verified,
          phone: updatedUser.phone,
          phone_verified: updatedUser.phone_verified,
          name: `${updatedUser.first_name || ''} ${updatedUser.last_name || ''}`.trim(),
          first_name: updatedUser.first_name,
          last_name: updatedUser.last_name,
          middle_name: updatedUser.middle_name,
          date_of_birth: updatedUser.date_of_birth,
          country_of_origin: updatedUser.country_of_origin,
          profile_complete: updatedUser.profile_complete,
        };
        localStorage.setItem('userData', JSON.stringify(safeUser));
      }
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Error completing profile:', err);
      const raw = err.response?.data?.detail ?? err.response?.data?.error;
      const backendDetail = Array.isArray(raw) ? raw[0]?.msg ?? raw[0] ?? raw : raw;
      const message = typeof backendDetail === 'string' ? backendDetail : (backendDetail?.message || backendDetail?.msg);
      setError(message || t('errorOccurred', 'An error has occurred. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const isPhoneSignup = signupMethod === 'phone';

  return (
    <div className="profile-setup">
      <div className="profile-card">
        <h2 className="profile-title">
          {isEditMode
            ? t('editProfileTitle', 'Edit your profile')
            : t('completeProfileTitle', 'Complete your profile')}
        </h2>
        <p className="profile-subtitle">
          {isEditMode
            ? t('editProfileSubtitle', 'Update your name, date of birth and country of origin.')
            : t(
                'completeProfileSubtitle',
                'We use these details for your immigration paperwork and notifications.'
              )}
        </p>

        <form className="profile-form" onSubmit={handleSubmit}>
          {/* Always show both: Email and Phone. One is read-only, the other has verify flow. */}
          <div className="form-group">
            <label htmlFor="email">{t('emailForImmigration', 'Email')}</label>
            {isPhoneSignup ? (
              <>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const next = e.target.value;
                    setEmail(next);
                    const normalized = normalizeEmail(next);
                    setEmailError(normalized && !isStrictEmail(normalized) ? t('invalidEmail', 'Please enter a valid email address.') : '');
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData?.getData('text') || '';
                    const normalized = normalizeEmail(`${email}${pasted}`);
                    setEmailError(normalized && !isStrictEmail(normalized) ? t('invalidEmail', 'Please enter a valid email address.') : '');
                  }}
                  placeholder={t('yourEmail', 'Your email')}
                  autoComplete="email"
                  readOnly={!!emailVerified}
                  className={emailVerified ? 'readonly' : ''}
                />
                {emailError ? <div className="login-error">{emailError}</div> : null}
                {!emailVerified && (
                  <div className="profile-verify-row">
                    <input
                      type="text"
                      placeholder={t('verificationCode', 'Verification code')}
                      value={emailOtpCode}
                      onChange={(e) => setEmailOtpCode(e.target.value)}
                      maxLength={8}
                      className="profile-otp-input"
                    />
                    {!emailOtpSent ? (
                      <button
                        type="button"
                        className="profile-verify-btn"
                        onClick={handleRequestEmailOtp}
                        disabled={emailVerifyLoading || !email?.trim() || !isStrictEmail(email)}
                      >
                        {emailVerifyLoading ? t('sending', 'Sending…') : t('sendCode', 'Send code')}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="profile-verify-btn"
                          onClick={handleVerifyEmailOtp}
                          disabled={emailVerifyLoading || !emailOtpCode.trim()}
                        >
                          {emailVerifyLoading ? t('verifying', 'Verifying…') : t('verify', 'Verify')}
                        </button>
                        <button
                          type="button"
                          className="profile-verify-btn profile-verify-btn-secondary"
                          onClick={handleRequestEmailOtp}
                          disabled={emailResendCooldown > 0 || emailVerifyLoading}
                        >
                          {emailResendCooldown > 0 ? `${emailResendCooldown}s` : t('resend', 'Resend')}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {emailVerified && <span className="profile-verified-badge">{t('verified', 'Verified')}</span>}
              </>
            ) : (
              <>
                <input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  className="readonly"
                  placeholder={t('emailReadOnly', 'From your account')}
                />
                <span className="profile-verified-badge">{t('verified', 'Verified')}</span>
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('phoneNumber', 'Phone number (WhatsApp)')}</label>
            {isPhoneSignup ? (
              <>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  readOnly
                  className="readonly"
                  placeholder={t('phoneReadOnly', 'Verified phone number')}
                />
                <span className="profile-verified-badge">{t('verified', 'Verified')}</span>
              </>
            ) : (
              <>
                <PhoneInput
                  country="es"
                  enableSearch
                  value={phone && !phone.startsWith('google:') ? phone.replace(/^\+/, '') : ''}
                  onChange={(value) => setPhone(value ? `+${value}` : '')}
                  inputProps={{ id: 'phone', name: 'phone' }}
                  disabled={phoneVerified}
                  className={phoneVerified ? 'readonly' : ''}
                />
                {!phoneVerified && (
                  <div className="profile-verify-row">
                    <input
                      type="text"
                      placeholder={t('verificationCode', 'Verification code')}
                      value={phoneOtpCode}
                      onChange={(e) => setPhoneOtpCode(e.target.value)}
                      maxLength={8}
                      className="profile-otp-input"
                    />
                    {!phoneOtpSent ? (
                      <button
                        type="button"
                        className="profile-verify-btn"
                        onClick={handleRequestPhoneOtp}
                        disabled={phoneVerifyLoading || !(phone && phone.replace(/^\+/, '').length >= 10)}
                      >
                        {phoneVerifyLoading ? t('sending', 'Sending…') : t('sendCode', 'Send OTP')}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="profile-verify-btn"
                          onClick={handleVerifyPhoneOtp}
                          disabled={phoneVerifyLoading || !phoneOtpCode.trim()}
                        >
                          {phoneVerifyLoading ? t('verifying', 'Verifying…') : t('verify', 'Verify')}
                        </button>
                        <button
                          type="button"
                          className="profile-verify-btn profile-verify-btn-secondary"
                          onClick={handleRequestPhoneOtp}
                          disabled={phoneResendCooldown > 0 || phoneVerifyLoading}
                        >
                          {phoneResendCooldown > 0 ? `${phoneResendCooldown}s` : t('resend', 'Resend')}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {phoneVerified && <span className="profile-verified-badge">{t('verified', 'Verified')}</span>}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="firstName">{t('firstName', 'First (given) name')} *</label>
            <input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(capitalizeWords(e.target.value))}
              required
              autoComplete="given-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="middleName">{t('middleName', 'Middle name(s) (optional)')}</label>
            <input
              id="middleName"
              type="text"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              autoComplete="additional-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">{t('lastName', 'Last / family name')} *</label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(capitalizeWords(e.target.value))}
              required
              autoComplete="family-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">{t('dateOfBirth', 'Date of birth')} *</label>
            <input
              id="dateOfBirth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
              max={new Date().toISOString().slice(0, 10)}
              autoComplete="bday"
            />
          </div>

          <div className="form-group">
            <label htmlFor="countryOfOrigin">{t('countryOfOrigin', 'Country of origin')} *</label>
            <select
              id="countryOfOrigin"
              value={countryOfOrigin}
              onChange={(e) => setCountryOfOrigin(e.target.value)}
              required
              autoComplete="country"
            >
              <option value="">{t('selectCountry', 'Select country')}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="login-error">{error}</div>}

          <button
            type="submit"
            className="login-button"
            disabled={
              isLoading ||
              (signupMethod === 'oauth' && !phoneVerified) ||
              (signupMethod === 'phone' && !emailVerified)
            }
          >
            {isLoading ? (
              <span className="loading-spinner" />
            ) : isEditMode ? (
              t('saveChanges', 'Save changes')
            ) : (
              t('saveAndContinue', 'Save & continue')
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProfileSetup;
